import type { Chat, Message } from "../types";

/**
 * Whether a message was written by the user who owns the chat.
 *
 * `createdBy` is the sender's uid at write time. A chat that was carried over
 * from an anonymous session at sign-in (session adoption) keeps its old
 * messages' `createdBy` until the server has moved them, and chats adopted
 * before 2026-09-04 keep the anonymous uid forever — the chat document records
 * that uid as `adoptedFromUid`. Comparing against `chat.userId` alone turned
 * every one of the user's messages into the character's on such a chat, which
 * also made the thread replay the whole history as a "fresh opening". The
 * `type === "user"` fallback mirrors the server's own rule
 * (apps/functions/src/functions/on-message-created.ts) and covers adopted chats
 * that predate `adoptedFromUid`.
 *
 * Pure and Firebase-free so it can be used from render code and unit tests.
 */
export function isOwnMessage(
  message: Pick<Message, "createdBy" | "type">,
  chat: Pick<Chat, "userId" | "adoptedFromUid">
): boolean {
  if (message.type === "system") return false;
  return (
    message.createdBy === chat.userId ||
    (typeof chat.adoptedFromUid === "string" &&
      chat.adoptedFromUid.length > 0 &&
      message.createdBy === chat.adoptedFromUid) ||
    message.type === "user"
  );
}

/** Whether a `lastMessages` participant key belongs to the chat's user (see isOwnMessage). */
export function isOwnParticipant(
  participantId: string,
  chat: Pick<Chat, "userId" | "adoptedFromUid">
): boolean {
  return (
    participantId === chat.userId ||
    (typeof chat.adoptedFromUid === "string" &&
      chat.adoptedFromUid.length > 0 &&
      participantId === chat.adoptedFromUid)
  );
}
